import { GeoJsonFeatureFactory, RotationUnit } from "../geojson/GeoJsonFeatureFactory";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import { TreeNode } from "@shared/ui-native/Tree";
import LayerGroup from 'ol/layer/Group';
import VectorTile from "ol/layer/VectorTile";
import { PMTilesVectorSource } from "ol-pmtiles";
import Static from "ol/source/ImageStatic";
import { transform as olTransform } from 'ol/proj';

import { Auth } from "@shared/Auth";
import { ModelService } from "@shared/models/model-service";
import { GeojsonFeatureCollection } from '@shared/geojson/types';
import { IDrawing } from "./drawing";
import { LoadingManager } from "@shared/models/loading-manager";
import { RateLimiter } from "@shared/utils/rate-limiter";
import XYZ from "ol/source/XYZ";
import { DrawingLoaders } from "./drawing-loaders";
import TileLayer from "ol/layer/Tile";
import { Geometry } from "ol/geom";
import ImageLayer from "ol/layer/Image";

const projection = 'EPSG:3857';
const GEOJSON_FORMAT = new GeoJSON({
    featureProjection: projection
});

const BASE_URL: string = ModelService.getVizApiBaseUrl() + "/drawings";
const MAX_RADIAN_VALUE = Math.PI * 2;
const rateLimiter = new RateLimiter(10, 15);


type DrawingConfig = {
    tileMinZoom: number;
    tileMaxZoom: number;
    rasterMinZoom: number;
    rasterMaxZoom: number;
    polygonMinZoom: number;
    polygonMaxZoom: number;
    textMinZoom: number;
    textMaxZoom: number;
}

const MOBILE_CONFIG: DrawingConfig = {
    tileMinZoom: 15,
    tileMaxZoom: 27,
    rasterMinZoom: 1,
    rasterMaxZoom: 16,
    polygonMinZoom: 18,
    polygonMaxZoom: 27,
    textMinZoom: 17,
    textMaxZoom: 27,
}

const DESKTOP_CONFIG: DrawingConfig = {
    tileMinZoom: 16,
    tileMaxZoom: 27,
    rasterMinZoom: 1,
    rasterMaxZoom: 16,
    polygonMinZoom: 17,
    polygonMaxZoom: 27,
    textMinZoom: 17,
    textMaxZoom: 27,
}


export class RasterDrawing implements IDrawing {
    uuid: string;
    node: TreeNode;

    wgs84Extent: olExtent.Extent;
    extent: olExtent.Extent;

    vectorLayer: VectorTile;
    rasterLayer: TileLayer<XYZ>;
    textLayer: VectorLayer<VectorSource<Geometry>>;
    bigTextLayer: VectorLayer<VectorSource<Geometry>>;
    polygonLayer: VectorLayer<VectorSource<Geometry>>;

    imageLayer: ImageLayer<Static>;

    layerGroup: LayerGroup;
    isMobile: boolean;

    constructor(node: TreeNode, isMobile = false) {
        this.uuid = node.uuid;
        this.node = node;
        this.layerGroup = new LayerGroup({});
        this.isMobile = isMobile;
    }


    public async load() {
        const config = this.isMobile ? MOBILE_CONFIG : DESKTOP_CONFIG;

        const status = await ModelService.getDrawingStatus(this.node);
        const preGeneratedRasterTilesAvailable: boolean = status.raster_tiles_available

        
        const rasterMinZoom = config.rasterMinZoom;
        const tileMinZoom = config.tileMinZoom;
        const rasterMaxZoom =  this.isMobile && preGeneratedRasterTilesAvailable ?
            status.max_raster_zoom : config.rasterMaxZoom;
        
        const pmTilesUrl: string | null = await ModelService.getPMTilesUrl(this.node);
        const vectorTilesAvailable: boolean = status.pmtiles_available && pmTilesUrl !== null;


        const detailedMinZoom = preGeneratedRasterTilesAvailable && this.isMobile ?
            status.max_raster_zoom : tileMinZoom;


        console.log("#####", this.node.text, "#####");

        if (preGeneratedRasterTilesAvailable) {

            // The batch processing has pregenerated a XYZ tile layer for this drawing. 
            // Use it.

            console.log(`Loading static image as base layer. [${1} - ${14}]`);
            await this.createImageLayer(1, 14);

            console.log(`Loading XYZ tiles as base layer. [${14} - ${detailedMinZoom}]`);
            await this.createRasterTileLayer(status.raster_tile_url, 14, rasterMaxZoom);
        } else {

            // No pregenerated tiles found.
            // Load one static image instead which covers the whole drawing.
            console.log(`Loading static image as base layer. [${rasterMinZoom} - ${rasterMaxZoom}]`)
            await this.createImageLayer(rasterMinZoom, rasterMaxZoom);
        }

        if (vectorTilesAvailable) {
            // If we show vector tiles below zoom level 20 on mobile the amount of geometry
            // loaded could still cause the browser to crash on mobile devices. The threshold 20
            // isn't based on anything else than trying out different threshold until finding a
            // good balance.  
            const vectorTileMinZoomLevel = (this.isMobile && !preGeneratedRasterTilesAvailable) ? 20 : detailedMinZoom;


            console.log(`Loading PMtiles. [${vectorTileMinZoomLevel} - ${27}]`);
            await this.createVectorLayer(pmTilesUrl, vectorTileMinZoomLevel, 27);
        } else {

            // If we cant find the vector tiles we just create a dynamic raster layer that loads
            // rasterized tiles from the Viz API. The server will generate the tiles on-demand.
            // This might be slow on some files which have a large amount of geometry.

            console.log(`Loading on-demand raster tiles. [${detailedMinZoom} - ${27}]` )
            await this.createDynamicRasterLayer(detailedMinZoom, 27);
        }


        if (this.isMobile && !preGeneratedRasterTilesAvailable) {
            // If we are on a mobile device we need to fill the gap between the raster layer and
            // the vector layer. If no pregenerated raster tiles have been created and vector tiles
            // are only shown at zoom level 20+ this layer will cover zoom levels 17-20 for example
            
            console.log(`[MOBILE] Loading on-demand raster tiles. [${detailedMinZoom} - ${20}]`)
            await this.createDynamicRasterLayer(detailedMinZoom, 20);
        }


        console.log(`Loading text layers. [${config.textMinZoom} - ${config.textMaxZoom}]`)
        await this.createTextLayer(config.textMinZoom, config.textMaxZoom);
        console.log(`Loading polygon layers. [${config.polygonMinZoom} - ${config.polygonMaxZoom}]`)
        await this.createPolygonLayer(config.polygonMinZoom, config.polygonMaxZoom);

        console.log("############");
    }


    private async createVectorLayer(pmtilesUrl: string, minZoom: number, maxZoom: number) {
        const source = new PMTilesVectorSource({
            url: pmtilesUrl,
        });

        this.vectorLayer = new VectorTile({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            source: source,
            maxZoom: maxZoom,
            minZoom: minZoom,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            extent: this.extent,
        });

        this.layerGroup.getLayers().push(this.vectorLayer);
    }

    private async createDynamicRasterLayer(minZoom: number, maxZoom: number) {
        const tileSource = new XYZ({
            url: `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/tiles/{x}/{y}/{z}`,
            tileLoadFunction: async (tile, src) => {
                await DrawingLoaders.imageResponseTileLoader(tile, src);
            }
        });


        this.rasterLayer = new TileLayer({
            source: tileSource,
            minZoom: minZoom,
            maxZoom: maxZoom,
            extent: this.extent,
        });

        this.layerGroup.getLayers().push(this.rasterLayer);
    }


    private async createRasterTileLayer(url: string, minZoom: number, maxZoom: number) {
        const tileSource = new XYZ({
            url: url,
            tileLoadFunction: async (tile, src) => {
                await DrawingLoaders.imageTileLoader(tile, src);
            }
        });

        this.rasterLayer = new TileLayer({
            source: tileSource,
            minZoom: minZoom,
            maxZoom: maxZoom,
            extent: this.extent,
        });

        this.layerGroup.getLayers().push(this.rasterLayer);
    }

    private async createPolygonLayer(minZoom: number, maxZoom: number) {
        const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/polygons`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: { "Authorization": "Bearer " + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();
        const polygonFeatures = GEOJSON_FORMAT.readFeatures(features, { featureProjection: 'EPSG:3857' });
        const polygonSource = new VectorSource({
            format: GEOJSON_FORMAT,
            features: polygonFeatures
        });

        this.polygonLayer = new VectorLayer({
            source: polygonSource,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            minZoom: minZoom,
            maxZoom: maxZoom,

        });

        this.layerGroup.getLayers().push(this.polygonLayer);
    }

    private async createTextLayer(minZoom: number, maxZoom: number) {

        LoadingManager.startLoading(this.uuid + "_text");
        const returnToken: () => void = await rateLimiter.requestToken();
        try {
            const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/text`

            const token = await Auth.getToken();

            const resp = await fetch(url, {
                headers: { "Authorization": "Bearer " + token.accessToken },
            });

            if (!resp.ok) {
                return;
            }

            const features: GeojsonFeatureCollection = await resp.json();

            const textFeatures = GEOJSON_FORMAT.readFeatures(features);


            const rotations: number[] = textFeatures.map(feature => feature.get("ROTATION") || 0);

            const isRadians: boolean = rotations.every(rotation => rotation >= 0 && rotation <= MAX_RADIAN_VALUE);

            if (isRadians) {
                textFeatures.forEach(feature => feature.set("ROTATION_UNITS", RotationUnit.RADIANS));
            } else {
                textFeatures.forEach(feature => feature.set("ROTATION_UNITS", RotationUnit.DEGREES));
            }

            const bigTextFeatures = textFeatures.filter(text => text.get("HEIGHT") > 50);

            const vectorSource = new VectorSource({
                features: textFeatures.filter(text => !bigTextFeatures.includes(text)),
            });

            this.textLayer = new VectorLayer({
                source: vectorSource,
                style: GeoJsonFeatureFactory.STYLE.textStyle,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            if (bigTextFeatures.length > 0) {
                const bigTextVectorSource = new VectorSource({
                    features: bigTextFeatures,
                });

                this.bigTextLayer = new VectorLayer({
                    source: bigTextVectorSource,
                    style: GeoJsonFeatureFactory.STYLE.textStyle,
                    minZoom: 1,
                    maxZoom: maxZoom,
                });

                this.layerGroup.getLayers().push(this.bigTextLayer);
            }


            this.layerGroup.getLayers().push(this.textLayer);

        } catch (e) {
            console.error(e);
        } finally {
            returnToken();
            LoadingManager.finishLoading(this.uuid + "_text");
        }
    }

    private async createImageLayer(minZoom: number, maxZoom: number) {
        const returnToken: () => void = await rateLimiter.requestToken();
        try {
            const url = `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/img/zoom/16`

            const token = await Auth.getToken();

            const resp = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token.accessToken}`
                }
            });

            const data = await resp.json();


            const minCoord = [data.extent[0], data.extent[1]];
            const maxCoord = [data.extent[2], data.extent[3]];
            const min = olTransform(minCoord, "EPSG:4326", "EPSG:3857");
            const max = olTransform(maxCoord, "EPSG:4326", "EPSG:3857");

            const transformedExtent: olExtent.Extent = olExtent.createEmpty();
            olExtent.extendCoordinates(transformedExtent, [min, max]);

            this.wgs84Extent = [data.extent[0], data.extent[1], data.extent[2], data.extent[3]];
            this.extent = transformedExtent;
            const source = new Static({
                url: data.url,
                projection: projection,
                imageExtent: transformedExtent,
                crossOrigin: 'anonymous'
            });

            this.imageLayer = new ImageLayer({
                source: source,
                minZoom: minZoom,
                maxZoom: maxZoom,
            });

            this.layerGroup.getLayers().push(this.imageLayer);

        } catch (e) {
            console.error(e);
        } finally {
            returnToken();
        }
    }


    public dispose() {

        if (this.vectorLayer) {
            this.vectorLayer.dispose();
        }

        if (this.rasterLayer) {
            this.rasterLayer.dispose();
        }

        if (this.textLayer) {
            this.textLayer.dispose();
        }

        if (this.bigTextLayer) {
            this.bigTextLayer.dispose();
        }

        if (this.polygonLayer) {
            this.polygonLayer.dispose();
        }

        if (this.imageLayer) {
            this.imageLayer.dispose();
        }
    }

}
